/* FleekPure · video reels carousel
 * - Arrows scroll the rail and hide at either end
 * - Muted previews play only while a card is on screen (one network request per visible card)
 * - Clicking a card opens a full-screen player with sound, with previous / next and Escape to close */

class FleekReels extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.rail = this.querySelector('[data-rail]');
    this.cards = [...this.querySelectorAll('.fr-card')];
    this.buttons = [...this.querySelectorAll('.fr-card__btn')];
    this.prev = this.querySelector('.fr-arrow--prev');
    this.next = this.querySelector('.fr-arrow--next');
    this.dialog = this.querySelector('[data-lightbox]');
    this.player = this.querySelector('[data-lightbox-video]');
    this.current = 0;

    if (!this.rail) return;

    this.#initArrows();
    this.#initPreviews();
    this.#initLightbox();
  }

  disconnectedCallback() {
    this._io?.disconnect();
    this._ro?.disconnect();
  }

  /* ───── Arrows ───── */
  #initArrows() {
    const update = () => {
      const max = this.rail.scrollWidth - this.rail.clientWidth - 2;
      const scrollable = max > 0;
      [this.prev, this.next].forEach((b) => b && (b.hidden = !scrollable));
      if (this.prev) this.prev.disabled = this.rail.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.rail.scrollLeft >= max;
    };
    [this.prev, this.next].forEach((btn) =>
      btn?.addEventListener('click', () => {
        this.rail.scrollBy({ left: Number(btn.dataset.dir) * this.rail.clientWidth * 0.8 });
      })
    );
    this.rail.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    this._ro = new ResizeObserver(update);
    this._ro.observe(this.rail);
    update();
  }

  /* ───── Muted previews ───── */
  #initPreviews() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection?.saveData;
    if (this.dataset.autoplay !== 'true' || reduce || saveData || !('IntersectionObserver' in window)) return;

    this._io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          const video = card.querySelector('.fr-card__video');
          if (!video) return;
          if (entry.isIntersecting && !this.dialog?.open) {
            if (!video.src) video.src = video.dataset.previewSrc;
            video.play().then(() => card.classList.add('is-playing')).catch(() => {});
          } else {
            video.pause();
            card.classList.remove('is-playing');
          }
        });
      },
      { threshold: 0.6 }
    );
    this.cards.forEach((card) => this._io.observe(card));
  }

  #pausePreviews() {
    this.cards.forEach((card) => card.querySelector('.fr-card__video')?.pause());
  }

  #resumePreviews() {
    if (!this._io) return;
    this.cards.forEach((card) => {
      this._io.unobserve(card);
      this._io.observe(card);
    });
  }

  /* ───── Lightbox ───── */
  #initLightbox() {
    if (!this.dialog || !this.player) return;

    this.buttons.forEach((btn, i) => btn.addEventListener('click', () => this.open(i)));
    this.dialog.querySelector('[data-close]')?.addEventListener('click', () => this.close());
    this.dialog.querySelectorAll('[data-step]').forEach((btn) =>
      btn.addEventListener('click', () => this.open(this.current + Number(btn.dataset.step)))
    );
    this.dialog.addEventListener('close', () => this.#onClosed());
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog || e.target.classList.contains('fr-lightbox__inner')) this.close();
    });
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.open(this.current - 1);
      if (e.key === 'ArrowRight') this.open(this.current + 1);
    });

    const single = this.buttons.length < 2;
    this.dialog.querySelectorAll('[data-step]').forEach((b) => (b.hidden = single));
  }

  open(index) {
    const n = this.buttons.length;
    if (!n) return;
    this.current = (index + n) % n;
    const btn = this.buttons[this.current];

    this.#pausePreviews();
    this.player.src = btn.dataset.src;
    if (btn.dataset.poster) this.player.poster = btn.dataset.poster;
    else this.player.removeAttribute('poster');

    if (!this.dialog.open) {
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
    this.player.play().catch(() => {});
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  #onClosed() {
    this.player.pause();
    this.player.removeAttribute('src');
    this.player.load();
    document.documentElement.style.overflow = '';
    this.buttons[this.current]?.focus({ preventScroll: true });
    this.#resumePreviews();
  }
}

if (!customElements.get('fleek-reels')) customElements.define('fleek-reels', FleekReels);
